import html2pdf from 'html2pdf.js';

/**
 * PDF export for a deck.
 *
 * Separate from `pdf-export.ts` on purpose (F-04 spec §6): that module renders one
 * scrolling A4 document out of Markdown, this one turns a rendered deck into pages of
 * exactly one slide each. Sharing a file would mean one set of page settings serving
 * two shapes, and the artifact export is not worth risking.
 *
 * The deck arrives as a complete HTML document — the same string the preview iframe
 * shows — so the work here is getting it out of that document and into the app's own,
 * where html2pdf can reach it:
 *
 *   1. parse it with DOMParser (which never runs scripts),
 *   2. scope its stylesheet, because the deck declares `:root{--accent,--radius,…}`
 *      and the app's own shadcn variables use those very names — pasting the deck's
 *      `:root` into the page would repaint the whole app mid-export,
 *   3. lay every visible slide out as its own fixed-size page and let html2pdf break
 *      between them.
 *
 * Nothing from the deck is executed: `<script>` and the nav bar are dropped, and the
 * markup itself was escaped by the renderer when it was built.
 */

/** 16:9 at A4 landscape width. A slide fitted into a full A4 leaves 43mm of nothing. */
export const DECK_PDF_PAGE_WIDTH_MM = 297;
export const DECK_PDF_PAGE_HEIGHT_MM = 167;

/** Class the scoped stylesheet hangs off. */
export const DECK_PDF_SCOPE = 'deck-pdf-root';

/**
 * Rewrites the deck's `:root` blocks onto a scope class.
 *
 * `:root` is `<html>`, so a deck stylesheet dropped into the app would redefine
 * `--accent`, `--border`, `--muted` and `--radius` for every shadcn component on the
 * page. The variables have to keep working for the slides, which is exactly what a
 * class on their common ancestor gives.
 */
export function scopeDeckCss(css: string, scope = DECK_PDF_SCOPE): string {
  return css.replace(/(^|[\s,{}])(:root)\b/g, `$1.${scope}`);
}

/**
 * What has to change for print.
 *
 * On screen a deck is one 16:9 box with absolutely-positioned slides and exactly one
 * of them visible. On paper every slide is its own page, so each gets its own box.
 */
export function buildDeckPdfOverrides(scope = DECK_PDF_SCOPE): string {
  return `
.${scope}{position:relative;width:${DECK_PDF_PAGE_WIDTH_MM}mm;margin:0 auto}
.${scope} .deck-pdf-page{width:${DECK_PDF_PAGE_WIDTH_MM}mm;height:${DECK_PDF_PAGE_HEIGHT_MM}mm;
max-width:none;aspect-ratio:auto;border-radius:0;box-shadow:none;overflow:hidden;
break-after:page;page-break-after:always;break-inside:avoid;page-break-inside:avoid}
.${scope} .deck-pdf-page:last-child{break-after:auto;page-break-after:auto}
.${scope} .deck-slide{display:flex !important;position:absolute;inset:0;overflow:hidden}
.${scope} .deck-nav{display:none !important}
`;
}

/**
 * Опции экспорта.
 *
 * Своё описание, а не тип из библиотеки: html2pdf.js понимает `pagebreak` с версии
 * 0.10, а поставляемый с ней d.ts об этом поле не знает — и `jsPDF.format` там объявлен
 * уже, чем библиотека принимает.
 */
interface DeckPdfOptions {
  margin: number;
  filename: string;
  image: { type: 'jpeg' | 'png' | 'webp'; quality: number };
  html2canvas: Record<string, unknown>;
  jsPDF: { unit: string; format: [number, number]; orientation: 'portrait' | 'landscape' };
  pagebreak: { mode: string[]; after?: string; before?: string; avoid?: string };
}

/** Кусок воркера html2pdf, которым здесь пользуются. */
interface DeckPdfWorker {
  set: (options: DeckPdfOptions) => DeckPdfWorker;
  from: (element: HTMLElement) => DeckPdfWorker;
  save: () => Promise<void>;
}

export interface DeckPdfInput {
  /** Complete HTML document of the deck, as the backend renderer produced it. */
  html: string;
  /** Deck name; becomes the file name. */
  name: string;
}

/** Keeps a file name usable on every OS without turning it into a slug. */
function toFilename(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${cleaned || 'Презентация'}.pdf`;
}

/**
 * Builds the print layout: one `.deck-pdf-page` per visible slide.
 *
 * Exported for tests — the DOM it returns is the whole of what the PDF will contain,
 * and asserting on it is far more useful than asserting that html2pdf was called.
 */
export function buildDeckPdfContainer(doc: Document): HTMLDivElement {
  const root = document.createElement('div');
  root.className = DECK_PDF_SCOPE;

  const slides = Array.from(doc.querySelectorAll('.deck-slide'));

  for (const slide of slides) {
    const page = document.createElement('div');
    page.className = 'deck deck-pdf-page';

    const copy = document.importNode(slide, true) as HTMLElement;
    // On screen only `.is-active` is shown; on paper every page shows its own slide.
    copy.classList.add('is-active');
    copy.querySelectorAll('script').forEach((node) => node.remove());

    page.appendChild(copy);
    root.appendChild(page);
  }

  return root;
}

/**
 * Renders the deck to a PDF and hands it to the browser.
 *
 * Runs on the client because the alternative is a headless Chromium on a 1.6 GB VPS
 * (spec decision #6). Fourteen slides take a few seconds — the caller is expected to
 * show that something is happening.
 */
export async function exportDeckToPdf({ html, name }: DeckPdfInput): Promise<void> {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  const css = Array.from(parsed.querySelectorAll('style'))
    .map((node) => node.textContent ?? '')
    .join('\n');

  const container = buildDeckPdfContainer(parsed);
  if (!container.childElementCount) {
    throw new Error('В презентации нет ни одного видимого слайда');
  }

  const styleEl = document.createElement('style');
  styleEl.id = 'deck-pdf-styles';
  styleEl.textContent = `${scopeDeckCss(css)}\n${buildDeckPdfOverrides()}`;
  document.head.appendChild(styleEl);

  // No positioning on the container: html2pdf clones it into its own overlay, and
  // position:fixed/absolute on the clone breaks the html2canvas capture area — the
  // same trap pdf-export.ts documents.
  document.body.appendChild(container);
  await new Promise((done) => requestAnimationFrame(() => requestAnimationFrame(done)));

  try {
    const options: DeckPdfOptions = {
      margin: 0,
      filename: toFilename(name),
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: null },
      jsPDF: {
        unit: 'mm',
        // A page the exact shape of a slide, rather than a slide adrift on an A4.
        format: [DECK_PDF_PAGE_WIDTH_MM, DECK_PDF_PAGE_HEIGHT_MM],
        orientation: 'landscape' as const,
      },
      pagebreak: { mode: ['css', 'legacy'], after: '.deck-pdf-page' },
    };

    await (html2pdf() as unknown as DeckPdfWorker).set(options).from(container).save();
  } finally {
    container.remove();
    styleEl.remove();
  }
}
